import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { EdgeToEdge } from "@capawesome/capacitor-android-edge-to-edge-support";
import { StatusBar, Style } from "@capacitor/status-bar";
import { message } from "antd";
import { getDecryptedUserData } from "./helpers/bycrypt";

export const initializeCapacitor = async () => {
  if (!Capacitor.isNativePlatform()) return;

  const isIOS = Capacitor.getPlatform() === "ios";

  // Status bar / navigation bar
  if (Capacitor.getPlatform() === "android") {
    await EdgeToEdge.setStatusBarColor({ color: "#FFFFFF" });
    await EdgeToEdge.setNavigationBarColor({ color: "#FFFFFF" });
    // White background → use dark status bar icons/text so they stay visible
    await StatusBar.setStyle({ style: Style.Light });
  }

  // ---------------------------
  // Keyboard Aware Handler
  // ---------------------------

  const scrollFocusedElementIntoView = () => {
    setTimeout(() => {
      const active = document.activeElement as HTMLElement | null;

      if (!active) return;

      const isFocusable =
        active.tagName === "INPUT" ||
        active.tagName === "TEXTAREA" ||
        active.tagName === "SELECT" ||
        active.getAttribute("contenteditable") === "true" ||
        Boolean(active.closest(".ck-editor")) ||
        Boolean(active.closest(".ant-select"));

      if (!isFocusable) return;

      // Check if input is inside a modal
      const modal = active.closest(".ant-modal");
      if (modal) {
        // For modal inputs, scroll within modal body
        const modalBody = modal.querySelector(".ant-modal-body");
        if (modalBody) {
          const rect = active.getBoundingClientRect();
          const modalRect = modalBody.getBoundingClientRect();
          
          // Only scroll if element is outside modal body view
          if (rect.top < modalRect.top || rect.bottom > modalRect.bottom) {
            active.scrollIntoView({
              behavior: "smooth",
              block: "center",
              inline: "nearest",
            });
          }
        }
      } else {
        // For regular inputs, scroll normally
        active.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    }, 300);
  };

  Keyboard.addListener("keyboardWillShow", (info) => {
    console.log("keyboard will show with height:", info.keyboardHeight);

    document.body.classList.add("keyboard-open");
    document.body.style.setProperty(
      "--keyboard-height",
      `${info.keyboardHeight}px`,
    );

    if (isIOS) {
      document.body.classList.add("ios-keyboard-open");
      
      // For iOS modals, adjust the modal wrapper position to account for safe area
      const modalWraps = document.querySelectorAll(
        ".ant-modal-wrap:not(.ant-modal-wrap-hidden)"
      );
      modalWraps.forEach((wrap: Element) => {
        const htmlElement = wrap as HTMLElement;
        if (htmlElement.style.display === "none") return;
        
        // Get safe area insets
        const style = getComputedStyle(document.documentElement);
        const safeAreaTop = style.getPropertyValue("--safe-area-inset-top") || "0px";
        
        // Position modal to stay within safe area and above keyboard
        htmlElement.style.position = "fixed";
        htmlElement.style.top = safeAreaTop;
        htmlElement.style.bottom = `${info.keyboardHeight}px`;
        htmlElement.style.maxHeight = `calc(100dvh - ${safeAreaTop} - ${info.keyboardHeight}px)`;
      });
    }

    scrollFocusedElementIntoView();
  });

  Keyboard.addListener("keyboardDidShow", () => {
    scrollFocusedElementIntoView();
  });

  Keyboard.addListener("keyboardWillHide", () => {
    console.log("keyboard will hide");

    document.body.classList.remove("keyboard-open");
    document.body.style.removeProperty("--keyboard-height");

    if (isIOS) {
      document.body.classList.remove("ios-keyboard-open");
      
      // Reset modal wrapper styles
      const modalWraps = document.querySelectorAll(
        ".ant-modal-wrap:not(.ant-modal-wrap-hidden)"
      );
      modalWraps.forEach((wrap: Element) => {
        const htmlElement = wrap as HTMLElement;
        htmlElement.style.position = "";
        htmlElement.style.top = "";
        htmlElement.style.bottom = "";
        htmlElement.style.maxHeight = "";
      });
    }
  });

  document.addEventListener(
    "focusin",
    () => {
      scrollFocusedElementIntoView();
    },
    true,
  );

  // ---------------------------
  // App Lifecycle
  // ---------------------------

  App.addListener("appStateChange", ({ isActive }) => {
    console.log("App state changed. Is active?", isActive);
  });

  // ---------------------------
  // Android Hardware Back Button
  // ---------------------------

  // Attendance Tablet role (Check In/Out tab with SQLite + local face match):
  // the hardware back button must never close the app while the tablet flow
  // is active. The app is closed explicitly via the "Exit App" option in the
  // scanner's top-right menu.
  const isAttendanceTabletSession = () => {
    const userData = getDecryptedUserData();
    const roleName = String(
      userData?.role?.key || userData?.role?.name || "",
    ).toLowerCase();
    return (
      roleName.includes("attendance tablet") ||
      roleName.includes("attendance-tablet")
    );
  };

  const isTabletCheckInOutScreen = () => {
    const path = window.location.pathname;
    return path.includes("/face-scanner") || path.includes("/local-debug");
  };

  App.addListener("backButton", ({ canGoBack }) => {
    if (isAttendanceTabletSession() && isTabletCheckInOutScreen()) {
      if (canGoBack && !window.location.pathname.includes("/face-scanner")) {
        window.history.back();
        return;
      }
      return;
    }

    if (!canGoBack) {
      App.exitApp();
    } else {
      window.history.back();
    }
  });
};

export const getPlatformInfo = () => {
  return {
    platform: Capacitor.getPlatform(),
    isNative: Capacitor.isNativePlatform(),
    isWeb: !Capacitor.isNativePlatform(),
  };
};
